import { useOidc, useOidcAccessToken, useOidcIdToken, withOidcSecure } from "@axa-fr/react-oidc";
import { environment } from "@org-avp/avp-avengers-ui-shared-environment";
import { selectOidc, setUserData } from "@org-avp/avp-avengers-ui-shared-store";
import { FC, PropsWithChildren, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { OidcPaths } from "../utils/oidcRoutes";
import { OidcWaiting } from "./OidcStateComponents";

export interface OidcGuardProps {
  oidcPaths: OidcPaths;
}

export const OidcGuard: FC<PropsWithChildren<OidcGuardProps>> = ({ children, oidcPaths }) => {
  const OidcSecuredPageContent = withOidcSecure(OidcPageContent);
  return (
    <OidcInit oidcPaths={oidcPaths}>
      <OidcSecuredPageContent>
        {children}
      </OidcSecuredPageContent>
    </OidcInit>
  );
};

const OidcPageContent: FC<PropsWithChildren> = ({ children }) => {
  const oidcState = useSelector(selectOidc);
  return (
    <>
      {oidcState.userData == null
        ? <OidcWaiting/>
        : children}
    </>
  );
};

const OidcInit: FC<PropsWithChildren<OidcGuardProps>> = ({ children, oidcPaths }) => {
  const { accessToken, accessTokenPayload } = useOidcAccessToken();
  const { idToken, idTokenPayload } = useOidcIdToken();
  const { isAuthenticated } = useOidc();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(
    () => {
      const isUserDataComplete = isAuthenticated
        && typeof accessToken === "string" && typeof idToken === "string"
        && accessTokenPayload != null && typeof accessTokenPayload === "object"
        && idTokenPayload != null && typeof idTokenPayload === "object";

      if (isUserDataComplete) {
        const userInfo = {
          ...accessTokenPayload,
          ...idTokenPayload,
        };

        // only set user data, if the token is not expired already, and if the issuer matches the configured authority
        if ((userInfo.exp ?? 0) > Date.now() / 1000 && userInfo.iss === environment.NX_OIDC_AUTHORITY) {
          dispatch(setUserData({ userInfo, accessToken, idToken }));
          return;
        }

        // navigate to session lost page, if token is expired
        navigate("/" + oidcPaths.sessionLost);
      }

      dispatch(setUserData(null));
    },
    [accessToken, accessTokenPayload, dispatch, idToken, idTokenPayload, isAuthenticated, navigate, oidcPaths.sessionLost],
  );

  return (<>{children}</>);
};
